import { action, makeObservable, observable, runInAction } from "mobx";
import firebaseAgent from "../api/firebaseagent";
import { Coupon } from "../models/coupon";
import { BaseStore } from "./baseStore";
import { RootStore } from "./rootStore";

export default class CouponStore extends BaseStore<Coupon> {
     activeCoupon: Coupon | null = null;
     error: string | null = null;
     isLoading: boolean = false;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            activeCoupon: observable,
            error: observable,
            isLoading: observable,
            applyCoupon: action,
            clearCoupon: action
        });
    }

    protected getService() {
        return firebaseAgent.couponService;
    }

    /**
     * Validates and applies a coupon code to the current session.
     * Integrates with firebaseAgent.couponService.findByCode.
     */    
    applyCoupon = async (code: string, currentTotal: number) => {
        this.isLoading = true;
        this.error = null;
        
        try {
            // This returns a QuerySnapshot (a list of results)
            const snapshot: any = await firebaseAgent.couponService.findByCode(code);
            
            runInAction(() => {
                // 1. Check if the snapshot is empty
                if (!snapshot || snapshot.empty) {
                    this.error = "Coupon code not found.";
                    this.activeCoupon = null;
                    return;
                }

                // 2. Extract the data from the first document in the list
                const docSnap = snapshot.docs[0];
                const couponData = { id: docSnap.id, ...docSnap.data() } as Coupon;

                // 3. Business Logic Validation using extracted data
                if (!couponData.isActive) {
                    this.error = "This coupon is currently inactive.";
                    this.activeCoupon = null;
                    return;
                }

                // Handle validTo comparison (convert Firestore Timestamp to JS Date)
                const now = new Date();
                const validTo = couponData.validTo?.toDate 
                    ? couponData.validTo.toDate() 
                    : new Date(couponData.validTo as any);
                
                if (validTo < now) {
                    this.error = "This coupon has expired.";
                    this.activeCoupon = null;
                    return;
                }

                if (couponData.minPurchaseAmount && currentTotal < couponData.minPurchaseAmount) {
                    this.error = `Minimum purchase of Rs ${couponData.minPurchaseAmount} required.`;
                    this.activeCoupon = null;
                    return;
                }

                // Successful validation
                this.activeCoupon = couponData;
                this.error = null;
            });
        } catch (err) {
            runInAction(() => {
                this.error = "An error occurred while validating the coupon.";
                this.activeCoupon = null;
            });
        } finally {
            runInAction(() => { this.isLoading = false; });
        }
    }

    /**
     * Calculates the savings amount based on the current active coupon type.
     * Supports 'percentage' and 'fixed' discount logic.
     */
    calculateDiscountAmount = (subtotal: number): number => {
        if (!this.activeCoupon) return 0;

        const { discountType, discountValue, maxDiscountAmount } = this.activeCoupon;
        let discount = 0;

        if (discountType === 'percentage') {
            discount = (subtotal * discountValue) / 100;
            // Apply cap for percentage discounts if defined
            if (maxDiscountAmount && discount > maxDiscountAmount) {
                discount = maxDiscountAmount;
            }
        } else if (discountType === 'fixed') {
            discount = discountValue;
        }

        // Ensure discount doesn't exceed subtotal
        return Math.min(discount, subtotal);
    }
       
    clearCoupon = () => {
        this.activeCoupon = null;
        this.error = null;
    }
}